import { Component, computed, effect, inject, OnInit, signal, TemplateRef, viewChild } from '@angular/core';
import { FakeSearchbarComponent } from '@myarvato/components/fake-searchbar';
import { TitleComponent } from '@myarvato/shared/title';
import { TranslatePipe } from '@ngx-translate/core';
import { Message } from 'primeng/message';
import { Tab, TabList, TabPanel, TabPanels, Tabs } from 'primeng/tabs';
import { ButtonModule } from 'primeng/button';
import { TrackingTabComponent } from './components/tracking-tab/tracking-tab.component';
import { getSearchWidgetConfig, SearchWidgetComponent } from '@myarvato/components/search-widget';
import { SearchDtoBase } from '@myarvato/shared/models';
import { EmptyStateComponent } from './components/empty-state/empty-state.component';
import { CustomizationTabsService, UserTabDto } from '@myarvato/core/customization';
import { UserTrackingService } from '@myarvato/core/services';
import { CustomizationTranslationService } from '@myarvato/shared/services';
import { Router } from '@angular/router';
import { TrackingDetailsStore, TransportSearchService } from '@myarvato/modules/transport/services';
import { getDialogResult, MyArvatoDialogService } from '@myarvato/shared/dialog';
import { TRANSPORT_SEARCH_ALLOWED_MAX_LENGTH, TransportSearchIndexDto, TransportUserTabDataDto } from '@myarvato/modules/transport/models';

@Component({
  selector: 'transport-tracking-tabs',
  imports: [
    FakeSearchbarComponent,
    TitleComponent,
    TranslatePipe,
    Message,
    ButtonModule,
    Tabs,
    Tab,
    TabList,
    TabPanel,
    TabPanels,
    TrackingTabComponent,
    EmptyStateComponent,
  ],
  templateUrl: './tracking-tabs.component.html',
  styleUrl: './tracking-tabs.component.scss',
})
export class TrackingTabsComponent implements OnInit {
  // Injections
  private readonly customizationTabsService = inject(CustomizationTabsService<TransportUserTabDataDto>);
  private readonly userTrackingService = inject(UserTrackingService);
  private readonly customizationTranslationService = inject(CustomizationTranslationService);
  private readonly dialogService = inject(MyArvatoDialogService);
  private readonly router = inject(Router);
  private readonly trackingDetailsStore = inject(TrackingDetailsStore);

  // Change Detection
  protected readonly userTabs = signal<UserTabDto<TransportUserTabDataDto>[]>([]);
  protected readonly selectedTabName = signal<string>('');
  protected readonly loadedTabNames = signal<Set<string>>(new Set());
  protected readonly hasTabs = computed(() => this.userTabs().length > 0);
  protected readonly errorMessage = signal<string | undefined>(undefined);

  // View Children
  private readonly searchDialogRowTemplate = viewChild.required<
    TemplateRef<{
      $implicit: SearchDtoBase;
    }>
  >('searchDialogRowTemplate');

  // Fields
  private readonly tabsContext = 'transport-tabs-view';

  constructor() {
    effect(() => {
      const selectedItem = this.trackingDetailsStore.selectedTrackingItem();
      if (!selectedItem) {
        return;
      }

      this.handleTabAddition({
        context: this.tabsContext,
        displayName: selectedItem.trackingNumber,
        data: { transportId: selectedItem.ehGuid },
      });
    });
  }

  public ngOnInit(): void {
    this.loadUserTabs();
  }

  private loadUserTabs(): void {
    this.customizationTabsService.getTabs(this.tabsContext).subscribe({
      next: (data) => {
        this.userTabs.set(data || []);
        if (this.hasTabs()) {
          const firstTab = this.userTabs()[0];
          this.onTabChange(firstTab.displayName);
        }
      },
      error: (error) => {
        console.error('Error loading user tabs:', error);
        const errorCode = error?.error?.code || 'G0400';
        const errorTranslationTitle = this.customizationTranslationService.getTranslation(
          `errors.${errorCode}.title`,
          'errors.G0400.title',
        );
        const errorTranslationDescription = this.customizationTranslationService.getTranslation(
          `errors.${errorCode}.description`,
          'errors.G0400.description',
        );

        this.errorMessage.set(`${errorTranslationTitle}: ${errorTranslationDescription}`);
      },
      complete: () => {
        this.openRouterDataTabs();
      },
    });
  }

  private openRouterDataTabs(): void {
    // Preserve Angular navigationId, but clear any custom state after reading
    const currentState = history.state as UserTabDto<TransportUserTabDataDto> & { navigationId?: number };
    const newTab = currentState as UserTabDto<TransportUserTabDataDto>;

    if (newTab?.displayName) {
      this.handleTabAddition(newTab);
      const navId = currentState?.navigationId;
      // Clear custom state to avoid re-processing on reload/back, keep URL unchanged
      history.replaceState(navId ? { navigationId: navId } : null, '', this.router.url);
    }
  }

  protected onTabChange(tabName: string | undefined): void {
    if (!tabName) {
      return;
    }

    this.userTrackingService.trackEvent('Transport - Tabs', 'Tab Changed');

    this.selectedTabName.set(tabName);
    const loaded = new Set(this.loadedTabNames());
    if (!loaded.has(tabName)) {
      loaded.add(tabName);
      this.loadedTabNames.set(loaded);
    }
  }

  protected onTabClose(tab: UserTabDto<TransportUserTabDataDto>): void {
    this.userTrackingService.trackEvent('Transport - Tabs', 'Tab Closed');

    const updatedTabs = this.userTabs().filter((t) => t.displayName !== tab.displayName);
    this.userTabs.set(updatedTabs);
    const updatedLoadedTabs = new Set(this.loadedTabNames());
    updatedLoadedTabs.delete(tab.displayName);
    this.loadedTabNames.set(updatedLoadedTabs);

    if (updatedTabs.length > 0 && this.selectedTabName() === tab.displayName) {
      const firstTab = updatedTabs[0];
      this.onTabChange(firstTab.displayName);
    } else if (updatedTabs.length === 0) {
      this.selectedTabName.set('');
    }

    // Delete the tab from the backend
    if (tab.id) {
      this.customizationTabsService.deleteTab(tab.id).subscribe();
    }
  }

  protected onCloseAllTabs(): void {
    this.userTrackingService.trackEvent('Transport - Tabs', 'Close All Tabs');

    this.userTabs.set([]);
    this.loadedTabNames.set(new Set());
    this.selectedTabName.set('');

    // Clear all tabs in the backend
    this.customizationTabsService.deleteAllTabsOfContext(this.tabsContext).subscribe();
  }

  protected async onSearchbarClick() {
    this.userTrackingService.trackEvent('Transport - Tabs', 'Search Clicked');

    const searchDialog = this.dialogService.showDialog(SearchWidgetComponent, {
      ...getSearchWidgetConfig(TransportSearchService),
      inputValues: {
        maxSearchLength: TRANSPORT_SEARCH_ALLOWED_MAX_LENGTH,
        rowTemplate: this.searchDialogRowTemplate(),
        trackerCategory: 'Transport - Tabs - Search',
        translationKey: 'pages.transport.search-widget',
      },
    });

    const result = await getDialogResult<TransportSearchIndexDto>(searchDialog);
    if (!result?.ehGuid) {
      return;
    }

    const newTab: UserTabDto<TransportUserTabDataDto> = {
      displayName: result.trackingNumber,
      context: this.tabsContext,
      data: { transportId: result.ehGuid },
    };

    this.handleTabAddition(newTab);
  }

  private handleTabAddition(newTab: UserTabDto<TransportUserTabDataDto>): void {
    const existingTab = this.userTabs().find((tab) => tab.displayName === newTab.displayName);
    if (existingTab) {
      this.onTabChange(existingTab.displayName);
    } else {
      this.userTabs.update((tabs) => [...tabs, newTab]);
      this.onTabChange(newTab.displayName);

      // Save the new tab
      this.customizationTabsService.addTab(newTab).subscribe({
        next: (savedTab) => {
          // Update the tab with the saved ID
          this.userTabs.update((tabs) => {
            const index = tabs.findIndex((tab) => tab.displayName === newTab.displayName);
            if (index !== -1) {
              tabs[index].id = savedTab.id;
            }
            return tabs;
          });
        },
      });
    }
  }
}
